/**
 * Countdown — 3 variants (thin_bar, centered, split)
 *
 * 2 タイプを自動切替するフェーズドカウントダウン:
 *  - 開始前 (now < startDate)        → 「開催まで」 (label = startLabel ?? "開催まで")
 *  - 開催中 (startDate <= now < endDate) → 「終了まで」 (label = duringLabel ?? "終了まで")
 *  - 終了後 (endDate ?? startDate 以降)    → expiredMessage
 *
 * endDate を指定しない場合: startDate に達したら即「終了」状態
 *
 * SSR 対策: 初回 render は target 基準で計算（hydration ミスマッチ回避）
 */
import { useEffect, useMemo, useState } from 'react';
import type { CountdownContent } from '../schemas/countdown';
import { Body, Btn, Eyebrow, H2, SectionFrame } from './primitives';

interface CD {
  content: CountdownContent;
}

type Phase = 'before' | 'during' | 'expired';

interface PhasedCountdown {
  phase: Phase;
  /** カウントダウン対象の時刻 ms (expired のとき null) */
  targetMs: number | null;
  /** 現在のフェーズで表示するラベル ("開催まで" / "終了まで") */
  label: string;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  finished: boolean;
}

function parseDate(s: string | null | undefined): number | null {
  if (!s) return null;
  const t = Date.parse(s);
  return Number.isFinite(t) ? t : null;
}

function computeDelta(targetMs: number, nowMs: number) {
  const diff = Math.max(0, targetMs - nowMs);
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

function resolvePhase(
  startMs: number | null,
  endMs: number | null,
  nowMs: number,
  labels: { start: string; during: string },
): { phase: Phase; targetMs: number | null; label: string } {
  if (startMs === null) {
    return { phase: 'expired', targetMs: null, label: '' };
  }
  if (nowMs < startMs) {
    return { phase: 'before', targetMs: startMs, label: labels.start };
  }
  if (endMs !== null && nowMs < endMs) {
    return { phase: 'during', targetMs: endMs, label: labels.during };
  }
  return { phase: 'expired', targetMs: null, label: '' };
}

/** content から start/end/labels を解釈し、現在フェーズに応じた残り時間を返す。 */
function usePhasedCountdown(content: CountdownContent): PhasedCountdown {
  const startMs = useMemo(
    () => parseDate(content.startDate ?? content.targetDate ?? null),
    [content.startDate, content.targetDate],
  );
  const endMs = useMemo(() => parseDate(content.endDate ?? null), [content.endDate]);
  const labels = useMemo(
    () => ({
      start: content.startLabel?.trim() || '開催まで',
      during: content.duringLabel?.trim() || '終了まで',
    }),
    [content.startLabel, content.duringLabel],
  );

  const [state, setState] = useState<PhasedCountdown>(() => {
    const nowMs = typeof window === 'undefined' ? (startMs ?? 0) : Date.now();
    const resolved = resolvePhase(startMs, endMs, nowMs, labels);
    const delta =
      resolved.targetMs !== null
        ? computeDelta(resolved.targetMs, nowMs)
        : { days: 0, hours: 0, minutes: 0, seconds: 0 };
    return { ...resolved, ...delta };
  });

  useEffect(() => {
    if (startMs === null) {
      setState({
        phase: 'expired',
        targetMs: null,
        label: '',
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
      });
      return;
    }
    const tick = () => {
      const nowMs = Date.now();
      const resolved = resolvePhase(startMs, endMs, nowMs, labels);
      const delta =
        resolved.targetMs !== null
          ? computeDelta(resolved.targetMs, nowMs)
          : { days: 0, hours: 0, minutes: 0, seconds: 0 };
      setState({ ...resolved, ...delta });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startMs, endMs, labels]);

  return state;
}

/** PhasedCountdown を TimeLeft 互換（既存 CountdownGroup 用）に変換 */
function asTimeLeft(p: PhasedCountdown): TimeLeft {
  return {
    days: p.days,
    hours: p.hours,
    minutes: p.minutes,
    seconds: p.seconds,
    finished: p.phase === 'expired',
  };
}

const UNIT_LABELS = { days: '日', hours: '時間', minutes: '分', seconds: '秒' } as const;
type Unit = keyof typeof UNIT_LABELS;

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

/** カウントダウン数字グループ（4 セット）。サイズに応じて bigSize でレイアウト分岐 */
function CountdownGroup({
  time,
  size = 'lg',
}: {
  time: TimeLeft;
  size?: 'sm' | 'lg';
}) {
  const units: Array<{ key: Unit; value: number }> = [
    { key: 'days', value: time.days },
    { key: 'hours', value: time.hours },
    { key: 'minutes', value: time.minutes },
    { key: 'seconds', value: time.seconds },
  ];
  const numFs = size === 'lg' ? 'clamp(2.5rem, 5vw + 0.5rem, 4.5rem)' : '1.5rem';
  const labelFs = size === 'lg' ? '0.7rem' : '0.65rem';
  const gap = size === 'lg' ? 24 : 12;
  return (
    <div
      role="timer"
      aria-live="polite"
      style={{
        display: 'flex',
        gap,
        alignItems: 'flex-end',
        fontFamily: 'var(--ls-font-heading)',
      }}
    >
      {units.map((u, i) => (
        <div
          key={u.key}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
            position: 'relative',
            paddingRight: i < units.length - 1 ? gap / 2 : 0,
          }}
        >
          <span
            style={{
              fontSize: numFs,
              lineHeight: 1,
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '-0.02em',
            }}
          >
            {pad(u.value)}
          </span>
          <span
            style={{
              fontSize: labelFs,
              color: 'var(--ls-text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontWeight: 600,
            }}
          >
            {UNIT_LABELS[u.key]}
          </span>
        </div>
      ))}
    </div>
  );
}

/** 細帯バリアント: 横一列 [logo] [text + phase label] [countdown] [cta] */
export function CountdownThinBar({ content }: CD) {
  const phase = usePhasedCountdown(content);
  const time = asTimeLeft(phase);
  if (time.finished) {
    return (
      <section
        className="ls-section"
        style={{
          background: 'var(--ls-accent-soft)',
          borderTop: '1px solid var(--ls-border)',
          borderBottom: '1px solid var(--ls-border)',
          padding: '14px 0',
        }}
      >
        <div
          className="ls-section-inner"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            paddingTop: 0,
            paddingBottom: 0,
          }}
        >
          <span style={{ fontWeight: 600 }}>
            {content.expiredMessage || '終了しました'}
          </span>
        </div>
      </section>
    );
  }
  return (
    <section
      className="ls-section"
      style={{
        background: 'var(--ls-accent-soft)',
        borderTop: '1px solid var(--ls-border)',
        borderBottom: '1px solid var(--ls-border)',
        padding: '14px 0',
      }}
    >
      <div
        className="ls-section-inner"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 24,
          flexWrap: 'wrap',
          paddingTop: 0,
          paddingBottom: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          {content.logoUrl && (
            <img
              src={content.logoUrl}
              alt=""
              style={{ height: 28, width: 'auto', objectFit: 'contain' }}
            />
          )}
          {content.headline && (
            <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>{content.headline}</span>
          )}
          {phase.label && (
            <span
              style={{
                fontSize: '0.75rem',
                color: 'var(--ls-text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              {phase.label}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
          <CountdownGroup time={time} size="sm" />
          {content.primaryCta && (
            <Btn variant="primary" href={content.primaryCtaHref ?? '#'}>
              {content.primaryCta}
            </Btn>
          )}
        </div>
      </div>
    </section>
  );
}

/** 中央寄せバリアント: 縦並びの目立つカウントダウン */
export function CountdownCentered({ content }: CD) {
  const phase = usePhasedCountdown(content);
  const time = asTimeLeft(phase);
  return (
    <SectionFrame>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 24,
          textAlign: 'center',
        }}
      >
        {content.logoUrl && (
          <img
            src={content.logoUrl}
            alt=""
            style={{ height: 56, width: 'auto', objectFit: 'contain', marginBottom: 8 }}
          />
        )}
        {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
        {content.headline && (
          <H2 style={{ textAlign: 'center', whiteSpace: 'pre-wrap' }}>{content.headline}</H2>
        )}
        {content.description && (
          <Body style={{ maxWidth: 600, textAlign: 'center' }}>{content.description}</Body>
        )}
        {time.finished ? (
          <div style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--ls-text-muted)' }}>
            {content.expiredMessage || '終了しました'}
          </div>
        ) : (
          <>
            {phase.label && (
              <div
                style={{
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: 'var(--ls-text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                {phase.label}
              </div>
            )}
            <div style={{ padding: '8px 0' }}>
              <CountdownGroup time={time} size="lg" />
            </div>
          </>
        )}
        {content.primaryCta && !time.finished && (
          <Btn variant="primary" href={content.primaryCtaHref ?? '#'}>
            {content.primaryCta}
          </Btn>
        )}
      </div>
    </SectionFrame>
  );
}

/** 左右分割バリアント: 左にテキスト+CTA、右にカウントダウン */
export function CountdownSplit({ content }: CD) {
  const phase = usePhasedCountdown(content);
  const time = asTimeLeft(phase);
  return (
    <SectionFrame background="surface">
      <div className="ls-split-pair">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {content.logoUrl && (
            <img
              src={content.logoUrl}
              alt=""
              style={{ height: 40, width: 'auto', objectFit: 'contain', marginBottom: 4 }}
            />
          )}
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          {content.headline && (
            <H2 style={{ whiteSpace: 'pre-wrap' }}>{content.headline}</H2>
          )}
          {content.description && <Body>{content.description}</Body>}
          {content.primaryCta && !time.finished && (
            <div style={{ marginTop: 8 }}>
              <Btn variant="primary" href={content.primaryCtaHref ?? '#'}>
                {content.primaryCta}
              </Btn>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          {time.finished ? (
            <div
              style={{
                padding: 32,
                border: '2px dashed var(--ls-border)',
                borderRadius: 'var(--ls-radius-lg)',
                fontSize: '1.125rem',
                fontWeight: 600,
                color: 'var(--ls-text-muted)',
              }}
            >
              {content.expiredMessage || '終了しました'}
            </div>
          ) : (
            <>
              {phase.label && (
                <div
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'var(--ls-text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                  }}
                >
                  {phase.label}
                </div>
              )}
              <CountdownGroup time={time} size="lg" />
            </>
          )}
        </div>
      </div>
    </SectionFrame>
  );
}

export const countdownVariants = {
  thin_bar: CountdownThinBar,
  centered: CountdownCentered,
  split: CountdownSplit,
};
